import bellToneSrc from '@/assets/sounds/bell-tone.wav';

type CountdownBeepController = {
  stop: () => void;
  isActive: () => boolean;
};

type CountdownAlarmOptions = {
  repeatCount?: number;
  gapMs?: number;
  volume?: number;
  onStop?: () => void;
};

const DEFAULT_REPEAT_COUNT = 3;
const DEFAULT_GAP_MS = 140;
const DEFAULT_VOLUME = 1;

export const startCountdownBeep = (
  options: CountdownAlarmOptions = {},
): CountdownBeepController | null => {
  if (typeof window === 'undefined') return null;

  const {
    repeatCount = DEFAULT_REPEAT_COUNT,
    gapMs = DEFAULT_GAP_MS,
    volume = DEFAULT_VOLUME,
    onStop,
  } = options;

  let isStopped = false;
  let playCount = 0;
  let replayTimeoutId: number | null = null;

  const safeRepeatCount = Math.max(1, Math.floor(repeatCount));
  const safeGapMs = Math.max(0, Math.floor(gapMs));
  const safeVolume = Math.max(0, Math.min(1, volume));
  const audio = new Audio(bellToneSrc);
  audio.preload = 'auto';
  audio.volume = safeVolume;

  const stop = () => {
    if (isStopped) return;
    isStopped = true;

    if (replayTimeoutId !== null) {
      window.clearTimeout(replayTimeoutId);
      replayTimeoutId = null;
    }

    try {
      audio.pause();
      audio.currentTime = 0;
    } catch {
      // Ignore cleanup errors when audio is already disposed.
    }

    onStop?.();
  };

  const isActive = () => !isStopped;

  const playNext = () => {
    if (isStopped) return;
    if (playCount >= safeRepeatCount) {
      stop();
      return;
    }
    playCount += 1;

    try {
      audio.currentTime = 0;
      void audio.play();
    } catch {
      stop();
    }
  };

  audio.onended = () => {
    if (isStopped) return;
    if (playCount >= safeRepeatCount) {
      stop();
      return;
    }
    replayTimeoutId = window.setTimeout(() => {
      replayTimeoutId = null;
      playNext();
    }, safeGapMs);
  };

  playNext();

  return {
    stop,
    isActive,
  };
};

